import {
  exteriorMasonrySlotNames,
  exteriorMaterialSlotNames,
} from "building-types/materials/exteriorMaterialSlotNames";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ExteriorMasonry, ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { InteriorMaterial, InteriorPalette, RoomMaterials } from "building-types/materials/interiorPaletteTypes";
import type { Palette } from "building-types/materials/palette";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import { PaletteType } from "building-types/materials/paletteType";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import { MasonryDataById } from "common/materials/library/masonryDataById";
import { allMasonryMaterialSrcs, masonryMaterialSrc } from "common/materials/masonryMaterialSrc";
import type { MasonryId } from "common/materials/masonryTypes";
import { MasonryTrimKind } from "common/materials/masonryTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { isMasonryId, isDesignMaterialSrc } from "common/materials/materialSrc";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { defaultExteriorPalette } from "./settings/defaultExteriorMaterials";

/**
 * Only exported for testing purposes.
 */
export function splitPaletteSlotId(kind: string): { prefix: string; slotNumber: number } {
  const match = kind.match(/\d/);
  assertIsNotNullOrUndefined(match, "Material slot ID should contain a number at the end");
  const firstDigitIndex = match.index ?? kind.length;
  const prefix = kind.substring(0, firstDigitIndex);
  const slotNumber = parseInt(kind.substring(firstDigitIndex));
  return { prefix, slotNumber };
}

function initRoomTypeLookupCache(palette: InteriorPalette) {
  const cache = (palette.roomTypeLookupCache = {});

  for (const roomPalette of palette.roomMaterials) {
    for (const roomType of roomPalette.roomTypes) {
      if (cache[roomType]) {
        cache[roomType] = { ...cache[roomType], ...roomPalette };
      } else {
        cache[roomType] = roomPalette;
      }
    }
  }

  palette.isRoomTypeLookupCacheDirty = false;
}

// prevents duplicate interior material errors from being logged
export const materialLookupErrorCache: { [materialId: string]: boolean } = {};

function interiorMaterialNameByRoomType(
  palette: InteriorPalette,
  roomType: RoomType,
  kind: InteriorMaterial,
  logIfMissing = true,
): MaterialSrc | undefined {
  if (!palette.roomTypeLookupCache || palette.isRoomTypeLookupCacheDirty) {
    initRoomTypeLookupCache(palette);
  }

  const { defaultRoomMaterials: defaultRoomPalette, roomTypeLookupCache } = palette;

  const roomPalette: RoomMaterials = roomTypeLookupCache?.[roomType] || defaultRoomPalette;

  // shortcut for common case
  const matId: MaterialSrc | undefined = roomPalette[kind] || defaultRoomPalette[kind];
  if (isDesignMaterialSrc(matId)) {
    return matId;
  }

  // fall through to palette material id with lowest index
  const { prefix, slotNumber } = splitPaletteSlotId(kind);
  let fallbackSlotNumber = slotNumber;

  while (fallbackSlotNumber > 0) {
    --fallbackSlotNumber;
    const matId: MaterialSrc | undefined =
      roomPalette[(prefix + fallbackSlotNumber) as InteriorMaterial] ||
      defaultRoomPalette[(prefix + fallbackSlotNumber) as InteriorMaterial];
    if (isDesignMaterialSrc(matId)) {
      return matId;
    }
  }

  if (logIfMissing && !materialLookupErrorCache[kind]) {
    materialLookupErrorCache[kind] = true;
    console.error("Could not find material ID: " + kind);
  }
  return undefined;
}

function interiorMaterialNameNoRoomType(palette: InteriorPalette, kind: InteriorMaterial) {
  if (!palette.roomTypeLookupCache || palette.isRoomTypeLookupCacheDirty) {
    initRoomTypeLookupCache(palette);
  }

  const { defaultRoomMaterials: defaultRoomPalette } = palette;

  // shortcut for common case
  const matId: MaterialSrc | undefined = defaultRoomPalette[kind];
  if (isDesignMaterialSrc(matId)) {
    return matId;
  }

  // fall through to palette material id with lowest index
  const { prefix, slotNumber } = splitPaletteSlotId(kind);
  let fallbackSlotNumber = slotNumber;

  while (fallbackSlotNumber > 0) {
    --fallbackSlotNumber;
    const matId: MaterialSrc | undefined = defaultRoomPalette[(prefix + fallbackSlotNumber) as InteriorMaterial];
    if (isDesignMaterialSrc(matId)) {
      return matId;
    }
  }

  console.error("Could not find material ID: " + kind);
  return undefined;
}

export function getInteriorMaterialSrcByKindNoFallback(
  palette: InteriorPalette,
  kind: InteriorMaterial,
): MaterialSrc | undefined {
  const matId: MaterialSrc | undefined = palette.defaultRoomMaterials[kind];
  if (isDesignMaterialSrc(matId)) {
    return matId;
  }
  return undefined;
}

/** Palette material slots that have a material assigned on that slot (not empty), matching Palette inspector behavior. */
export function nonEmptyPaletteMaterialChoices(
  palette: ExteriorPalette,
  materialChoices: readonly ExteriorMaterial[],
  currentMaterial?: ExteriorMaterial,
): ExteriorMaterial[];
export function nonEmptyPaletteMaterialChoices(
  palette: InteriorPalette,
  materialChoices: readonly InteriorMaterial[],
  currentMaterial?: InteriorMaterial,
): InteriorMaterial[];
export function nonEmptyPaletteMaterialChoices<T extends string>(
  palette: Palette,
  materialChoices: readonly T[],
  currentMaterial?: T,
): T[] {
  return materialChoices.filter((slot) => {
    if (currentMaterial !== undefined && slot === currentMaterial) {
      return true;
    }

    if (palette.type === PaletteType.EXTERIOR_PALETTE) {
      return getMaterialSrcByKindNoFallback(palette, slot as ExteriorMaterial) !== undefined;
    }

    return getInteriorMaterialSrcByKindNoFallback(palette, slot as InteriorMaterial) !== undefined;
  });
}

// Return the MaterialSrc object from a palette entry. In case the palette entry is a numerical masonry id, the base material of the masonry is returned.
export function getMaterialSrcByKindNoFallback(
  palette: ExteriorPalette,
  kind: ExteriorMaterial,
): MaterialSrc | undefined {
  const matId: MaterialSrc | MasonryId = palette[kind];
  if (matId === undefined || matId === null) {
    return undefined;
  }
  if (isMasonryId(matId)) {
    const baseMaterialSrc = masonryMaterialSrc(matId, MasonryTrimKind.Base);
    if (baseMaterialSrc !== undefined) {
      return baseMaterialSrc;
    }
    return undefined;
  }
  if (isDesignMaterialSrc(matId)) {
    return matId;
  }
  return undefined;
}

export function getAllMaterialSrcFromPalette(materials: ExteriorPalette): MaterialSrc[] {
  const materialSrcSet: Set<MaterialSrc> = new Set();
  for (const kind of exteriorMaterialSlotNames) {
    const matId: MaterialSrc | undefined = materials[kind];
    if (matId === undefined || matId === null) {
      continue;
    } else if (isDesignMaterialSrc(matId)) {
      materialSrcSet.add(matId);
    }
  }
  for (const kind of exteriorMasonrySlotNames) {
    const matId: MaterialSrc | MasonryId | undefined = materials[kind];
    if (matId === undefined || matId === null) {
      continue;
    }
    if (isMasonryId(matId)) {
      const masonryMaterialSrcs = allMasonryMaterialSrcs(matId);
      if (masonryMaterialSrcs !== undefined) {
        for (const trimKind of Object.values(MasonryTrimKind)) {
          const trimSrc = masonryMaterialSrcs[trimKind];
          if (trimSrc !== undefined) {
            materialSrcSet.add(trimSrc);
          }
        }
      }
    } else if (isDesignMaterialSrc(matId)) {
      materialSrcSet.add(matId);
    }
  }
  return [...materialSrcSet];
}

/** Get a masonry trim set from a palette, with fallback to palette slots with a lower index.
 * Can also return a MaterialSrc if that slot in the palette is set to directly point to MaterialSrc. */
function exteriorMasonryFromPalette(
  materials: ExteriorPalette,
  kind: ExteriorMasonry,
  failIfMissing = false,
): MasonryId | MaterialSrc | undefined {
  if (kind === undefined) {
    console.error("Empty material");
    return undefined;
  }

  // common case
  const masonryId: MasonryId | MaterialSrc | undefined = materials[kind];
  if (
    (isMasonryId(masonryId) && MasonryDataById[masonryId] !== undefined) ||
    isDesignMaterialSrc(masonryId as MaterialSrc)
  ) {
    return masonryId;
  }

  // fall through palette material ids with lower indices
  const { prefix, slotNumber } = splitPaletteSlotId(kind);
  let fallbackSlotNumber = slotNumber;

  while (fallbackSlotNumber > 0) {
    --fallbackSlotNumber;
    const masonryId: MasonryId | MaterialSrc = materials[prefix + fallbackSlotNumber];
    if (
      (isMasonryId(masonryId) && MasonryDataById[masonryId] !== undefined) ||
      isDesignMaterialSrc(masonryId as MaterialSrc)
    ) {
      return masonryId;
    }
  }
  if (failIfMissing) {
    console.error("Could not find masonry kind: " + kind);
    return undefined;
  }
  return exteriorMasonryFromPalette(defaultExteriorPalette(), kind, true);
}

function exteriorMasonryMaterialSrcFromPalette(
  materials: ExteriorPalette,
  masonryKind: ExteriorMasonry,
  trimKind: MasonryTrimKind,
): MaterialSrc | undefined {
  const masonryId = exteriorMasonryFromPalette(materials, masonryKind, false);
  if (isMasonryId(masonryId)) {
    const trimMaterialSrc = masonryMaterialSrc(masonryId, trimKind);
    if (trimMaterialSrc !== undefined) {
      return trimMaterialSrc;
    }
    return masonryMaterialSrc(masonryId, MasonryTrimKind.Base);
  }
  return masonryId;
}

function exteriorMaterialSrcFromPalette(
  materials: ExteriorPalette,
  kind: ExteriorMaterial | undefined,
  failIfMissing = false,
): MaterialSrc | undefined {
  if (kind === undefined) {
    console.error("Empty material");
    return undefined;
  }

  if (kind.indexOf(":") >= 0) {
    const [masonryKind, trimKind] = kind.split(":");
    return exteriorMasonryMaterialSrcFromPalette(
      materials,
      masonryKind as ExteriorMasonry,
      trimKind as MasonryTrimKind,
    );
  }
  if (!((exteriorMasonrySlotNames as string[]).indexOf(kind) < 0)) {
    console.assert(false, "Lookups of masonry materials should specify 'masonry kind' like 'Base' or 'Header' - see migration with maxSchemaVersion 0.0.81");
  }

  // Look up the requested id first
  const src: MaterialSrc | undefined = getMaterialSrcByKindNoFallback(materials, kind);
  if (src !== undefined) {
    return src;
  }

  // fall through palette material ids with lower indices
  const { prefix, slotNumber } = splitPaletteSlotId(kind);
  let fallbackSlotNumber = slotNumber;

  while (fallbackSlotNumber > 0) {
    --fallbackSlotNumber;
    const src: MaterialSrc | undefined = getMaterialSrcByKindNoFallback(
      materials,
      (prefix + fallbackSlotNumber) as ExteriorMaterial,
    );
    if (src !== undefined) {
      return src;
    }
  }
  if (failIfMissing) {
    console.error("Could not find material ID: " + kind);
    return undefined;
  }
  return exteriorMaterialSrcFromPalette(defaultExteriorPalette(), kind, true);
}

export function materialSrcFromPalette(
  palette: Palette,
  paletteSlot: PaletteMaterialSlot | undefined,
  roomType?: RoomType,
  logIfMissing?: boolean,
): MaterialSrc | undefined {
  if (paletteSlot === undefined) {
    console.error("Empty material");
    return undefined;
  }

  if (palette.type === PaletteType.EXTERIOR_PALETTE) {
    return exteriorMaterialSrcFromPalette(palette, paletteSlot as ExteriorMaterial);
  } else {
    return roomType
      ? interiorMaterialNameByRoomType(palette, roomType, paletteSlot as InteriorMaterial, logIfMissing)
      : interiorMaterialNameNoRoomType(palette, paletteSlot as InteriorMaterial);
  }
}
